/**
 * The named boundaries of a race plan — the points the plan is divided at.
 *
 * Single source of truth on purpose. The altitude chart and the Plan Map both
 * have to mark the same places; when each derived the set itself they drifted,
 * and the map ended up showing only the organiser's aid stations while the chart
 * showed every boundary, including the checkpoints the user had placed.
 *
 * Excluded here:
 *   - checkpoint rows       zero-length aid-station stops, drawn in their own
 *                           style from matchedCheckpoints
 *   - the finish            already the end of the route
 *   - duplicate distances   two boundaries at one spot mark one place
 *   - matched checkpoints   a boundary sharing a distance with an aid station is
 *                           that aid station; showing both stacks two markers
 */

export function getPlanBoundaries(racePlanSegments = [], matchedCheckpoints = []) {
  if (!racePlanSegments || racePlanSegments.length === 0) return [];

  const cpDistSet = new Set(
    (matchedCheckpoints || [])
      .filter((cp) => cp.fit_distance_km != null && cp.status !== 'undetected')
      .map((cp) => (cp.fit_distance_km * 1000).toFixed(0)),
  );

  const maxEndM = Math.max(...racePlanSegments.map((s) => s.end_distance_m || 0));
  const seen = new Set();
  const boundaries = [];

  for (const seg of racePlanSegments) {
    if (seg.segment_type === 'checkpoint') continue;
    if (seg.end_distance_m == null) continue;

    const key = seg.end_distance_m.toFixed(0);
    if (seen.has(key)) continue;
    seen.add(key);

    if (seg.end_distance_m >= maxEndM) continue;   // the finish
    if (cpDistSet.has(key)) continue;              // an aid station lives here

    boundaries.push({
      distanceM: seg.end_distance_m,
      distKm: seg.end_distance_m / 1000,
      label: seg.end_label,
      elapsed_s: seg.elapsed_s,
      lat: seg.end_lat ?? null,
      lon: seg.end_lon ?? null,
      // Set when the runner placed this one themselves.
      manualId: seg.manual_end_id || null,
    });
  }

  return boundaries;
}

/**
 * Those boundaries that can be drawn on a map.
 *
 * A boundary only gets coordinates when the track had a point close enough to
 * the distance being named (see _COORD_MAX_OFFSET_M in running_race_plan.py) —
 * a sparse stretch yields none rather than a misleading position, so this is
 * always a subset of getPlanBoundaries.
 */
export function getPlanBoundaryMarkers(racePlanSegments = [], matchedCheckpoints = []) {
  return getPlanBoundaries(racePlanSegments, matchedCheckpoints)
    .filter((b) => b.lat != null && b.lon != null)
    .map((b) => ({
      position: [b.lat, b.lon],
      name: b.label,
      distance: b.distKm,
      manualId: b.manualId,
    }));
}

/**
 * The checkpoints the watch will actually receive for a given granularity.
 *
 * MIRRORS `build_checkpoints` in backend/app/services/running/running_watch_plan.py.
 * Both must apply the same rule or the preview lies about what was published —
 * keep them in step when either changes.
 *
 *   detailed  every segment boundary, so the watch counts down to "Col Cornet"
 *             and not only to the next aid station
 *   minimal   aid stations, plus the checkpoints the runner placed themselves
 *
 * Deriving this rather than fetching the published URL is deliberate: serving
 * that URL increments the link's fetch_count and last_fetched_at, which are how
 * the user tells whether their watch has actually synced. A preview must not
 * forge those.
 *
 * @param segments  the DETAILED plan segments (the backend filters by
 *                  granularity itself, from the same full list)
 * @param granularity 'detailed' | 'minimal'
 */
export function getWatchPlanCheckpoints(segments = [], granularity = 'detailed') {
  const minimal = String(granularity).toLowerCase().startsWith('m');
  const byDistance = new Map();

  for (const seg of segments || []) {
    const isCp = seg.segment_type === 'checkpoint';
    const isManual = Boolean(seg.manual_end_id);
    if (minimal && !isCp && !isManual) continue;
    if (seg.end_distance_m == null) continue;

    const d = Math.round(Number(seg.end_distance_m));
    // A checkpoint or a user-placed point overwrites a plain landmark sharing
    // its distance; a landmark never displaces them.
    if (byDistance.has(d) && !isCp && !isManual) continue;
    byDistance.set(d, {
      distance_m: d,
      label: seg.end_label,
      kind: isCp ? 'checkpoint' : isManual ? 'manual' : 'landmark',
    });
  }

  return [...byDistance.values()].sort((a, b) => a.distance_m - b.distance_m);
}
